<template>
    <div :class="{ 'answerbank-with-publish-footer': publishGateEnabled && activeDirectory }">
        <div class="p-d-flex p-jc-between">
            <div class="p-d-flex p-ai-center" style="gap: 0.75rem">
                <p class="profile-title">Answer Bank</p>
                <span
                    v-if="publishGateEnabled && activeDirectory"
                    :class="['profile-status-pill', isPublished ? 'live' : 'draft']"
                    :title="isPublished && lastPublishedAt ? 'Last published ' + new Date(lastPublishedAt).toLocaleString() : ''"
                >
                    {{ isPublished ? 'LIVE' : 'DRAFT' }}
                </span>
            </div>

            <div v-if="activeDirectory" class="profile-completion-container">
                <p class="profile-completion-text">Your profile is {{ totalCompletionPercentage }}% completed</p>
                <ProgressBar style="height: 16px; border-radius: 10px;" :value="totalCompletionPercentage" />
            </div>

            <div class="p-d-flex p-ai-center" style="gap: 0.5rem">
                <!-- Top-of-page Publish button removed; the sticky bottom
                     footer (project_se_section_confirmation_publish_gate
                     Phase D) is now the single source of the Publish action. -->
                <div v-show="rootDirtyInd && showGlobalSaveCancel">
                    <Button label="Cancel" class="p-button p-component p-button-outlined button"
                        @click="cancelAnswerBank" />
                    <Button label="Save" class="p-button p-component button" @click="saveAnswerBank" />
                    <FloatingSaveCancel @save="saveAnswerBank" @cancel="cancelAnswerBank" />
                </div>
            </div>
        </div>

        <div class="directory-row">
            <TreeSelect v-model="activeDirectory" :options="directoryTreeData"
                placeholder="Select the Answer Bank to review"
                class="directory-select"
                selectionMode="single" @change="directoryChanged" />
            <div v-if="activeDirectoryTitle" class="directory-breadcrumb">
                <i class="pi pi-folder-open"></i>
                <span>{{ activeDirectoryTitle }}</span>
            </div>
            <div v-if="showInputSwitch && isAdmin" class="visibility-toggle">
                <label class="visibility-label">Visible to customers when published</label>
                <InputSwitch v-model="checked" @change="onCheckedClick" />
                <TooltipComponent
                    description="Toggle to ON if you want this vendor's services visible to customers; OFF if you want to hide this vendor's services from customers. Though ON, this vendor's services will only be visible if the vendor has also published."
                />
            </div>
        </div>
        <p v-if="!activeDirectory" class="directory-empty-hint">
            Choose a directory above to view and edit your profile answers.
        </p>

        <!-- Draft/Live status banner -- publish gate only, mirrors the
             DRAFT/LIVE pill in the header but spells out what it means and
             what to do next, instead of leaving the vendor to infer it. -->
        <div
            v-if="publishGateEnabled && activeDirectory && sectionStatus?.summary"
            :class="['profile-status-banner', isPublished ? 'live' : 'draft']"
        >
            <i :class="isPublished ? 'pi pi-check-circle' : 'pi pi-hourglass'"></i>
            <div class="profile-status-banner-text">
                <p class="profile-status-banner-title">
                    {{ isPublished
                        ? 'This profile is live — visible to clients'
                        : 'This profile is in draft — it is not yet visible to clients' }}
                </p>
                <p class="profile-status-banner-detail">
                    <template v-if="isPublished">
                        Profile published {{ lastPublishedAt ? new Date(lastPublishedAt).toLocaleDateString() : '—' }}
                        <template v-if="latestConfirmation">
                            · Last confirmed {{ new Date(latestConfirmation.confirmed_at).toLocaleDateString() }} by {{ latestConfirmation.confirmed_by }}
                        </template>
                        . Changes will take effect after re-confirmation.
                    </template>
                    <template v-else>
                        Confirm all required sections to publish your profile.
                        {{ sectionStatus.summary.confirmed_required_sections }} of
                        {{ sectionStatus.summary.total_required_sections }} sections confirmed.
                    </template>
                </p>
            </div>
        </div>

        <div v-if="isLoadingDirectory" class="directory-loading">
            <ProgressSpinner style="width: 36px; height: 36px" strokeWidth="4" />
            <p>Loading directory…</p>
        </div>

        <Accordion v-else :activeIndex="0" @click="onTabClick">
            <AccordionTab  v-for="(item, index) in templates" :key="index">
                <template #header>
                    <div class="accordion-header">
                        <span>{{ item.category_name }}</span>
                        <div class="accordion-header-right">
                            <span class="completion-indicator" :class="getCompletionClass(item.category_name)">
                                {{ getCompletionText(item.category_name) }}
                            </span>
                            <span
                                v-if="publishGateEnabled && getSectionState(item)?.is_current"
                                class="section-confirmed-pill"
                                :title="`Confirmed by ${getSectionState(item).latest_confirmation.confirmed_by} on ${new Date(getSectionState(item).latest_confirmation.confirmed_at).toLocaleDateString()}`"
                            >
                                ✓ Confirmed
                            </span>
                            <span
                                v-else-if="publishGateEnabled && getSectionState(item)?.is_required_section && countSectionBlocking(item) > 0"
                                class="section-blocking-pill"
                            >
                                {{ countSectionBlocking(item) }} pending
                            </span>
                            <span v-if="item.created_on && isNewCategory(item.created_on)" class="new-badge">NEW</span>
                        </div>
                    </div>
                </template>
                <div
                    v-if="publishGateEnabled && item.category_description"
                    class="section-purpose-strip"
                >
                    {{ item.category_description }}
                </div>
                <Wrapper :parentItem="item" v-model="inputs[item.category_name]" :answers="answers"
                    @update:modelValue="updateAnswers" @isValid="handleValidation" :disableEdit="(isChannel || isAdmin) && !hasAgreement"
                    :publishGateEnabled="publishGateEnabled" :sectionConfirmedAt="getSectionState(item)?.latest_confirmation?.confirmed_at || null" />

                <!-- Section confirmation footer. Renders only when publish
                     gate is on AND the section is one that requires
                     confirmation (is_required_section). Disabled state
                     surfaces the pending count; enabled state opens the
                     Confirm dialog. -->
                <div
                    v-if="publishGateEnabled && getSectionState(item)?.is_required_section"
                    class="section-confirm-footer"
                >
                    <div class="section-confirm-status">
                        <template v-if="countSectionBlocking(item) > 0">
                            <i class="pi pi-exclamation-circle" style="color: #b45309;"></i>
                            <span class="section-confirm-warning">
                                {{ countSectionBlocking(item) }} Required {{ countSectionBlocking(item) === 1 ? 'answer needs' : 'answers need' }} your attention before you can confirm this section.
                            </span>
                        </template>
                        <template v-else-if="getSectionState(item)?.is_current">
                            <i class="pi pi-check-circle" style="color: #15803d;"></i>
                            <span class="section-confirm-confirmed">
                                Confirmed by {{ getSectionState(item).latest_confirmation.confirmed_by }} on
                                {{ new Date(getSectionState(item).latest_confirmation.confirmed_at).toLocaleDateString() }}.
                                You can re-confirm anytime.
                            </span>
                        </template>
                        <template v-else>
                            <i class="pi pi-info-circle" style="color: #2563eb;"></i>
                            <span class="section-confirm-prompt">
                                Confirm this section once you've reviewed all answers.
                            </span>
                        </template>
                    </div>
                    <Button
                        :label="sectionConfirmingByName[item.category_name]
                            ? 'Confirming…'
                            : (getSectionState(item)?.is_current ? 'Re-confirm section' : 'Confirm section')"
                        :disabled="countSectionBlocking(item) > 0 || sectionConfirmingByName[item.category_name]"
                        class="section-confirm-button"
                        @click="confirmSection(item)"
                    />
                </div>
            </AccordionTab>
        </Accordion>
    </div>

    <!-- Pre-flight blocker: Required questions still empty, so Publish
         is held. Lines are clickable and jump to the question (opens
         the section accordion + scrolls + flashes the field). -->
    <Dialog
        v-model:visible="preFlightDialogOpen"
        :draggable="false"
        modal
        :style="{ width: '38rem' }"
        header="Required questions need answers"
    >
        <p class="preflight-subtitle">
            {{ preFlightMissing.length }} Required {{ preFlightMissing.length === 1 ? 'question is' : 'questions are' }}
            still empty. Click any item below to jump to it. Once every Required question is answered, come back and publish.
        </p>
        <div class="preflight-list">
            <div
                v-for="(group, sec) in preflightGroupedBySection"
                :key="sec"
                class="preflight-group"
            >
                <p class="preflight-group-label">{{ sec }}</p>
                <button
                    v-for="entry in group"
                    :key="entry.id"
                    type="button"
                    class="preflight-item"
                    @click="jumpToQuestion(entry)"
                >
                    {{ entry.display_name }}
                </button>
            </div>
        </div>
        <template #footer>
            <Button label="Close" @click="preFlightDialogOpen = false" />
        </template>
    </Dialog>

    <!-- Publish confirmation: all Required answered, last chance to bail. -->
    <Dialog
        v-model:visible="confirmPublishOpen"
        :draggable="false"
        modal
        :style="{ width: '28rem' }"
        header="Publish your profile?"
    >
        <p class="preflight-subtitle">
            Clients will see your answers in matching and search results from now on.
        </p>
        <template #footer>
            <Button label="Cancel" class="p-button-text" @click="cancelPublishConfirm" />
            <Button label="Publish" @click="confirmPublish" />
        </template>
    </Dialog>

    <!-- Save-to-Draft confirmation: a Live profile is editing a Required
         field back to empty. Confirming flips the profile to Draft AND
         commits the save; cancelling blocks the save entirely. -->
    <Dialog
        v-model:visible="draftConfirmOpen"
        :draggable="false"
        modal
        :style="{ width: '48rem', maxWidth: '92vw' }"
        header="Return your profile to Draft?"
    >
        <p class="preflight-subtitle">
            Saving these changes will leave
            {{ draftConfirmFields.length === 1 ? 'a Required question' : draftConfirmFields.length + ' Required questions' }}
            empty. Your profile will be hidden from clients until you re-Publish.
        </p>
        <ul class="preflight-flat-list">
            <li v-for="f in draftConfirmFields" :key="f.id">
                <strong>{{ f.section_name }}:</strong> {{ f.display_name }}
            </li>
        </ul>
        <template #footer>
            <Button label="Cancel" class="p-button-text" @click="cancelDraftConfirm" />
            <Button
                label="Save and return to Draft"
                class="draft-save-button"
                @click="confirmDraftAndSave"
            />
        </template>
    </Dialog>

    <!-- Phase D section-gate dialog: required sections not yet confirmed.
         Lists each pending section; clicking jumps to it. Same shape as the
         pre-flight Required-questions dialog but scoped to section state. -->
    <Dialog
        v-model:visible="sectionGateDialogOpen"
        :draggable="false"
        modal
        :style="{ width: '38rem' }"
        header="Confirm your sections before publishing"
    >
        <p class="preflight-subtitle">
            <template v-if="runSectionGateCheck().length === 1">
                One required section still needs your confirmation. Click it
                to jump there, review the answers, and click Confirm section.
            </template>
            <template v-else>
                {{ runSectionGateCheck().length }} required sections still need
                your confirmation. Click any below to jump there, review the
                answers, and click Confirm section.
            </template>
        </p>
        <div class="preflight-list">
            <div class="preflight-group">
                <button
                    v-for="s in runSectionGateCheck()"
                    :key="s.category_id"
                    type="button"
                    class="preflight-item"
                    @click="jumpToSection(s.header)"
                >
                    {{ s.header }}
                </button>
            </div>
        </div>
        <template #footer>
            <Button label="Close" @click="closeSectionGateDialog" />
        </template>
    </Dialog>

    <!-- Phase D sticky footer: persistent "X of N required sections
         confirmed" counter + Publish button. Only renders when publish
         gate is enabled (BPO and other untoggled question_sets keep their
         existing top-of-page Publish behavior). -->
    <div
        v-if="publishGateEnabled && activeDirectory && sectionStatus?.summary"
        class="profile-publish-footer"
    >
        <div class="profile-publish-footer-left">
            <span class="profile-publish-footer-counter">
                {{ sectionStatus.summary.confirmed_required_sections }} of
                {{ sectionStatus.summary.total_required_sections }} required
                sections confirmed
            </span>
            <ProgressBar
                v-if="sectionStatus.summary.total_required_sections > 0"
                :value="Math.round(
                    (sectionStatus.summary.confirmed_required_sections /
                     sectionStatus.summary.total_required_sections) * 100
                )"
                style="height: 6px; border-radius: 999px; width: 220px;"
            />
        </div>
        <div class="profile-publish-footer-right">
            <span v-if="isPublished" class="profile-publish-footer-live-text">
                Profile is LIVE
            </span>
            <Button
                v-if="!isPublished"
                label="Publish profile"
                class="profile-publish-footer-button"
                @click="onPublishClick"
            />
        </div>
    </div>

    <Toast />
</template>

<script>
import FloatingSaveCancel from '../components/common/FloatingSaveCancel.vue';
import CurrentUserService from '../service/CurrentUserService';
import AnswerBankService from '../service/AnswerBankService';
import { getAccount } from '../service/common';
import Wrapper from '../components/answerBank/Wrapper';
import _ from 'lodash';
import TooltipComponent from './../components/Tooltip.vue';
import { getIsChannel } from '../service/common';
import PartnerDetailsService from '../service/PartnerDetailsService'
import InputSwitch from 'primevue/inputswitch';

window.setTimeout(function () {
    window.onpopstate = () => {
        this.sectionTrackGA();
    };
}, 1);
window.setTimeout(function () {
    window.onbeforeunload = () => {
        this.sectionTrackGA();
    };
}, 1);
export default {
    components: {
        FloatingSaveCancel,
        Wrapper,
        TooltipComponent,
        InputSwitch,
    },
    name: 'AnswerBank',
    data() {
        return {
            activeTab: [0],
            saveInd: false,
            answerBankData: [],
            partnerId: null,
            isAdmin: false,
            isChannel: false,
            lastClicked: new Date().getTime(),
            accordionLabel: null,
            templates: [],
            // Two-tier model toggle echoed from /v2/template/answer-bank.
            // When true the AnswerBank surfaces the section purpose strip,
            // subgroup headers, Required/Recommended badges, multi-select
            // caps, weighted completeness, and the publish gate. Stays
            // false for BPO + any other question_set not curated for the
            // two-tier model. First consumer: SE.
            publishGateEnabled: false,
            // Phase 6 publish-gate state. is_published comes from Portal
            // Backend's vendor_profile_publication Lambda; missing row =
            // Draft. The three *Dialog flags drive PrimeVue Dialog modals
            // for the pre-flight blocker, the publish confirmation, and
            // the save-empties-Required confirmation respectively.
            isPublished: false,
            lastPublishedAt: null,
            preFlightDialogOpen: false,
            preFlightMissing: [],
            confirmPublishOpen: false,
            draftConfirmOpen: false,
            draftConfirmFields: [],
            pendingSavePayload: null,
            // Section-confirmation state (project_se_section_confirmation_publish_gate).
            // sectionStatus is the raw response from GET /sections;
            // sectionStatusByName is a lookup map keyed by category_name (the
            // template's identifier) for fast per-section UI render.
            // sectionConfirmingByName: per-category-name boolean — true while
            // the in-flight POST /section-confirm is running, used to disable
            // that single section's button without affecting other sections.
            sectionStatus: null,
            sectionStatusByName: {},
            sectionConfirmingByName: {},
            // Phase D: dialog state for "sections need confirmation" pre-flight
            // (mirrors preFlightDialogOpen but for the section-gate blocker).
            sectionGateDialogOpen: false,
            // Goes true the instant directoryChanged fires and stays
            // true until getAnswers() resolves, so the page swaps the
            // old directory's accordion for a centered spinner instead
            // of lingering on stale answers during the fetch window.
            isLoadingDirectory: false,
            inputs: {},
            answers: [],
            newAnswerBankData: [],
            rootDirtyInd: false,
            fieldErrors: {},
            hasErrors: false,
            checked: false,
            directoryTreeData: null,
            activeDirectory: null,
            activeDirectoryTitle: null,
            qsid: null,
            platform: process.env.VUE_APP_PLATFORM_ID,
            origin: null,
            distributorValues: null,
            defaultSubnetworkId: process.env.VUE_APP_DEFAULT_SUBNETWORK_ID,
            hasAgreement: false,
            // ✅ Performance optimization: Cache for completion calculations
            completionCache: null,
            completionCacheTime: 0,
            categoryCompletionCache: {},
            completionUpdateTimer: null,
        };
    },
    async created() {
        this.userService = new CurrentUserService();
        this.answerBankService = new AnswerBankService();
        this.partnerDetailsService = new PartnerDetailsService()
        this.userIsACX = this.userService.marketplaceUser.userRole.roleKey.startsWith('acx:');
        this.isAdmin = getAccount();
        this.isChannel = getIsChannel();
        // Fetch and filter the directory tree data
        let data = await this.answerBankService.getDirectoryTreeData();
        this.directoryTreeData = data.filter(item => {
            // Only keep Talent, Transformation, and Technology
            return ['Talent', 'Transformation', 'Technology'].includes(item.label);
        }).map(item => {
            if (item.label === 'Talent') {
                // Ensure the main Talent category does not have a star icon
                return {
                    ...item,
                    selectable: false, // Make Talent itself not selectable
                    children: [
                        {
                            label: 'BPO', // Example subcategory
                            key: 'Talent 1', // Use a different key from parent
                            selectable: true,
                            data: 2, // Pass the same data from parent
                            // icon: 'pi pi-star' // Add star icon to subcategory if needed
                        },
                        ...item.children
                    ]
                };
            }
            return item;
        });
    },
    async mounted() {
        // this.getTemplateForQuestionSet();
        // await this.getAnswers();
        // this.initializeInputs();
        localStorage.removeItem('qsid');
        this.updateQsid();
        this.getAccessData();
    },
    computed: {
        showInputSwitch() {
            return this.qsid && parseInt(this.qsid) > 1;
        },
        // Once a profile is Live, every save happens through that section's
        // own Confirm button (which now saves + confirms together) instead
        // of the global Save/Cancel pair -- a single global Save spanning
        // every section would have to silently skip whichever sections
        // aren't confirmed yet, which is confusing partial-save behavior.
        // Draft profiles (or any question_set without the publish gate,
        // e.g. BPO) keep today's global Save unchanged.
        showGlobalSaveCancel() {
            return !(this.publishGateEnabled && this.isPublished);
        },
        totalCompletionPercentage() {
            // ✅ OPTIMIZED: Use cached value if available and recent
            if (!this.templates?.length || !this.answers?.length || !this.activeDirectory) return 0;

            // Return cached value if less than 500ms old
            if (this.completionCache !== null && Date.now() - this.completionCacheTime < 500) {
                return this.completionCache;
            }

            let totalQuestions = 0;
            let totalAnswered = 0;

            this.templates.forEach(templateCategory => {
                const categoryAnswers = this.answers.find(a => a.category_name === templateCategory.category_name);
                if (categoryAnswers) {
                    const { questionsCount, answeredCount } = this.getCategoryCompletionCounts(templateCategory, categoryAnswers);
                    totalQuestions += questionsCount;
                    totalAnswered += answeredCount;
                }
            });


            console.log('Total Questions:-', totalQuestions, 'Total Answered:-', totalAnswered);

            return totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
        },

        // Groups the pre-flight blocker's missing list by section_name so
        // the dialog renders one labelled chunk per section, matching the
        // accordion order in the page below it.
        preflightGroupedBySection() {
            const grouped = {};
            for (const entry of this.preFlightMissing) {
                if (!grouped[entry.section_name]) grouped[entry.section_name] = [];
                grouped[entry.section_name].push(entry);
            }
            return grouped;
        },

        // Most recent section confirmation across the whole question set --
        // drives the "Last confirmed <date> by <name>" line in the
        // draft/live status banner. Null when nothing has been confirmed yet.
        latestConfirmation() {
            const sections = this.sectionStatus?.sections || [];
            let latest = null;
            for (const section of sections) {
                const conf = section.latest_confirmation;
                if (!conf?.confirmed_at) continue;
                if (!latest || new Date(conf.confirmed_at) > new Date(latest.confirmed_at)) {
                    latest = conf;
                }
            }
            return latest;
        },
    },
    watch: {
        templates: {
            handler() {
                this.updateQsid();
                this.getAccessData();
                this.initializeInputs();
                this.getAnswers();
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        // AI Pre-Population — vendor-side render helpers.
        // Backend returns per-question provenance
        // (source / confidence_band / source_label / source_excerpt /
        // requires_review); these helpers translate that into visual
        // states + footer text that the field renderers apply.

        // Classify a question row into one of five visual states:
        //   'plain'            — no border, no footer (default / vendor-entered)
        //   'ai-inferred'      — amber border + "AI inferred from X — review required"
        //                        (any AI-written answer, high or low
        //                        confidence — all bands require vendor
        //                        review before publish)
        //   'ai-unable'        — red border (Required + unanswered, regardless
        //                        of whether AI ever attempted the field)
        //   'dashed-recommended' — dashed border, no footer (unanswered Recommended)
        provenanceState(question) {
            if (!question) return 'plain';
            const src = question.source;
            const required = question.required === true
                || question.required === 'true'
                || question.required === 1;
            const hasValue = this.hasVendorAnswer(question);

            if (src === 'ai_inferred' || src === 'ai_extracted') return 'ai-inferred';
            if (required && !hasValue) return 'ai-unable';

            // Empty non-AI states: dashed for unanswered recommended
            if (!hasValue && !required && !src) return 'dashed-recommended';

            return 'plain';
        },

        // True if the question has a non-empty answer stored.
        hasVendorAnswer(question) {
            const v = question?.json_value;
            if (v === null || v === undefined) return false;
            if (typeof v === 'string' && v.trim() === '') return false;
            if (Array.isArray(v) && v.length === 0) return false;
            if (typeof v === 'object' && Object.keys(v).length === 0) return false;
            // single_select / single_select_search / boolean_select answers are
            // wrapped as { name: <value> } even when unanswered (getAnswers()
            // maps a null server value to { name: null }, not bare null).
            if (typeof v === 'object' && !Array.isArray(v) && 'name' in v) {
                const name = v.name;
                return name !== null && name !== undefined && String(name).trim() !== '';
            }
            return true;
        },

        // Footer note to render under the field. Returns null for plain state.
        provenanceFooter(question) {
            const state = this.provenanceState(question);
            if (state === 'ai-inferred') {
                const label = question?.source_label || 'the source';
                return `AI inferred from ${label} — review required`;
            }
            if (state === 'ai-unable') {
                if (question?.source === 'ai_unable') {
                    return 'AI was unable to answer — your input is required';
                }
                return 'Your input is required to publish this profile';
            }
            return null;
        },

        // Count of items in a section that still need vendor attention
        // (amber ai_inferred with requires_review, or red ai_unable on
        // a Required field). Drives the yellow section-header banner.
        sectionAttentionCount(section) {
            if (!section?.data) return 0;
            return section.data.filter((q) => {
                const state = this.provenanceState(q);
                return state === 'ai-inferred' || state === 'ai-unable';
            }).length;
        },

        async getAccessData() {
            let data = await this.answerBankService?.getDirectoryTreeData() || [];
            this.directoryTreeData = data.filter(item => {
                // Only keep Talent, Transformation, and Technology
                return ['Talent', 'Transformation', 'Technology'].includes(item.label);
            }).map(item => {
                if (item.label === 'Talent') {
                    // Ensure the main Talent category does not have a star icon
                    return {
                        ...item,
                        icon: '',//NO ICON
                        selectable: false, // Make Talent itself not selectable
                        children: [
                            {
                                label: 'BPO', // Example subcategory
                                key: 'Talent ', // Use a different key from parent
                                selectable: true,
                                data: 2, // Pass the same data from parent
                                icon: 'pi pi-star' // Add star icon to subcategory if needed
                            },
                            ...item.children
                        ]
                    };
                }
                return item;
            });

            const res = await this.answerBankService.getAnswerBankAccess();
            this.updateDirectoryTreeAccess(res.data.data);
        },
        updateDirectoryTreeAccess(accessData) {
            const updateTree = (tree) => {
                return tree.map((node) => {
                    const newNode = { ...node };
                    const matchingAccess = _.find(accessData, (access) => {
                        const accessName = access.full_name.trim();
                        const nodeKey = node.key.trim();
                        const qsid = parseInt(localStorage.getItem('qsid'));
                        return accessName === nodeKey && node.data === qsid;
                    });

                    // Only update checked state if this is the currently selected node
                    if (node.data === parseInt(localStorage.getItem('qsid'))) {
                        this.checked = matchingAccess ? true : false;
                    }

                    newNode.isActive = !!matchingAccess;

                    if (node.children) {
                        newNode.children = updateTree(node.children);
                    }

                    return newNode;
                });
            };

            this.directoryTreeData = updateTree(this.directoryTreeData);
        },
        async onCheckedClick() {
            const newValue = this.checked; // Store the new value
            try {
                const payload = {
                    qsid: parseInt(localStorage.getItem('qsid')),
                    access: newValue,
                };
                const response = await this.answerBankService.postAnswerBankAccess(payload);
                if (response.data && response.data.data) {
                    // Don't call updateDirectoryTreeAccess here as it might be resetting the state
                    const matchingNode = response.data.data.find(
                        access => access.qsid === parseInt(localStorage.getItem('qsid'))
                    );
                    // Only update the checked state if we got a valid response
                    this.checked = matchingNode ? matchingNode.access : newValue;
                }
            } catch (error) {
                console.error('Error updating access:', error);
                this.checked = !newValue; // Revert only on error
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error updating visibility',
                    detail: 'Failed to update visibility status',
                    life: 3000
                });
            }
        },
        updateQsid() {
            const storedQsid = localStorage.getItem('qsid');
            this.qsid = storedQsid ? JSON.parse(storedQsid) : null;
        },
        directoryChanged(event) {

            const selectedKey = Object.keys(event)[0];

            const findNestedItem = (items) => {
                for (let item of items) {
                    if (item.key === selectedKey) {
                        return item;
                    }
                    if (item.children) {
                        const found = findNestedItem(item.children);
                        if (found) return found;
                    }
                }
                return null;
            };

            const selectedItem = findNestedItem(this.directoryTreeData);

            // Convert 'Talent' string to number 2 before saving to localStorage
            const valueToStore = selectedItem.data === 'Talent' ? 2 : selectedItem.data;
            localStorage.setItem('qsid', JSON.stringify(valueToStore));

            this.updateQsid();

            // Wipe stale state from the previous directory so the user
            // does not see old answers/badges/pill flicker through. The
            // loading flag drives the centered spinner that fills the
            // gap until getAnswers() resolves.
            this.isLoadingDirectory = true;
            this.templates = [];
            this.answers = [];
            this.inputs = {};
            this.isPublished = false;
            this.lastPublishedAt = null;

            this.activeDirectoryTitle = selectedKey.replaceAll(':', ' > ');
            this.getTemplateForQuestionSet();
        },

        handleValidation({ isValid, fieldName, categoryName }) {
            if (!this.fieldErrors[categoryName]) {
                this.fieldErrors[categoryName] = {};
            }
            this.fieldErrors = {
                ...this.fieldErrors,
                [categoryName]: {
                    ...this.fieldErrors[categoryName],
                    [fieldName]: isValid,
                },
            };

            // Checks if any field has validation errors
            this.hasErrors = Object.values(this.fieldErrors).every((category) =>
                Object.values(category).every((value) => value === true)
            );
        },
        initializeInputs() {
            // Convert answers into an object
            this.answers.forEach((category) => {
                category.data.forEach((answer) => {
                    this.answers[answer.id] = answer.json_value;
                });
            });

            // Initialize inputs with values from answers
            this.templates?.forEach((category) => {
                this.inputs[category.category_name] = {};
                category.data.forEach((field) => {
                    this.inputs[category.category_name][field.id] = this.answers[field.id] || '';
                });
            });
        },

        updateAnswers(updatedAnswers) {
            this.rootDirtyInd = true;

            // ✅ OPTIMIZED: Only update the specific field that changed
            Object.keys(updatedAnswers).forEach((fieldId) => {
                // Update in answers array - find specific field only
                for (let category of this.answers) {
                    const fieldIndex = category.data.findIndex(f => f.id === fieldId);
                    if (fieldIndex !== -1) {
                        // Direct mutation is faster than creating new objects
                        category.data[fieldIndex].json_value = updatedAnswers[fieldId];
                        // Optimistic local edit-time, compared against the
                        // section's confirmed_at by provenanceState so the
                        // amber "review required" highlight shows THIS
                        // question the instant it's edited -- not only
                        // after a save round-trip re-fetches the real
                        // server updated_on. Overwritten by the authoritative
                        // value once getAnswers() re-fetches post-confirm.
                        category.data[fieldIndex].updated_on = new Date().toISOString();
                        // Marks this row as having a local, unsaved edit --
                        // the server-side `source` (e.g. 'ai_inferred')
                        // only flips to 'vendor_edited' after an actual
                        // save, which for a Live profile is deferred until
                        // Confirm. Without this flag the footer/icon would
                        // keep citing the stale AI source while the vendor
                        // is actively editing it. Cleared automatically the
                        // next time getAnswers() re-fetches (it rebuilds
                        // fresh row objects with no such flag).
                        category.data[fieldIndex]._locallyEdited = true;
                        break; // Stop once found
                    }
                }

                // Update in inputs object - direct assignment
                for (let category of this.templates) {
                    if (this.inputs[category.category_name]?.[fieldId] !== undefined) {
                        this.inputs[category.category_name][fieldId] = updatedAnswers[fieldId];
                        break; // Stop once found
                    }
                }
            });

            // Clear caches and schedule update after a short delay
            this.clearCompletionCaches();
            
            // Update completion cache after 100ms to batch multiple rapid changes
            clearTimeout(this.completionUpdateTimer);
            this.completionUpdateTimer = setTimeout(() => {
                this.updateCompletionCache();
            }, 100);
        },

        clearCompletionCaches() {
            // Reset all caches to force recalculation on next access
            this.completionCache = null;
            this.completionCacheTime = 0;
            this.categoryCompletionCache = {};
        },

        updateCompletionCache() {
            // Calculate and cache the completion percentage
            if (!this.templates?.length || !this.answers?.length || !this.activeDirectory) {
                this.completionCache = 0;
                this.completionCacheTime = Date.now();
                return;
            }

            let totalQuestions = 0;
            let totalAnswered = 0;

            this.templates.forEach(templateCategory => {
                const categoryAnswers = this.answers.find(a => a.category_name === templateCategory.category_name);
                if (categoryAnswers) {
                    const { questionsCount, answeredCount } = this.getCategoryCompletionCounts(templateCategory, categoryAnswers);
                    totalQuestions += questionsCount;
                    totalAnswered += answeredCount;
                }
            });

            this.completionCache = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
            this.completionCacheTime = Date.now();
        },

        scrollToEl(element, offset) {
            window.scrollBy({
                top: element.getBoundingClientRect().top - offset,
                behavior: 'smooth',
            });
        },

        onTabClick(event) {
            let labelNames = new Set();
            this.templates.forEach((item) => {
                labelNames.add(item.category_name);
            });

            if (labelNames.has(event.target.innerText)) {
                setTimeout(() => {
                    this.scrollToEl(event.target, 70);
                }, 250);
                this.sectionTrackGA(),
                    this.accordionLabel === event.target.innerText
                        ? (this.accordionLabel = null)
                        : (this.accordionLabel = event.target.innerText);
            }
        },

        sectionTrackGA() {
            const timeNow = new Date().getTime();
            if (timeNow > this.lastClicked) {
                const duration = timeNow - this.lastClicked;
                const seconds = Math.floor(duration / 1000);
                this.lastClicked = timeNow;

                let sectionTrackInfo = {
                    section_time_spend: seconds,
                    section_name: this.accordionLabel,
                    timestamp: timeNow,
                };

                if (this.accordionLabel !== null) {
                    this.$gtag.event('answerbank_section_track', sectionTrackInfo);
                }
            }
        },

        postAnswerBank(payload) {
            this.answerBankService.postAnswerBank(payload, this.isAdmin).then((res) => {
                if (res.status === 200) {
                    this.rootDirtyInd = false;
                    this.$toast.add({ severity: 'success', summary: res.data, detail: '', life: 3000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error while creating data', detail: res.data, life: 3000 });
                }
            });
        },

        cancelAnswerBank() {
            window.location.reload();
        },

        // Transforms one answer's json_value into the shape postAnswers'
        // payload expects, per field_type. Shared by saveAnswerBank (whole
        // profile) and confirmSection's Live-profile save (one section).
        getFieldPayloadValue(field, answer) {
            if (field.field_type === 'single_select' || field.field_type === 'single_select_search') {
                return answer.json_value.name;
            } else if (field.field_type === 'boolean_select') {
                return answer.json_value?.value ?? null;
            } else if (field.field_type === 'multi_select' || field.field_type === 'multi_select_search') {
                return Array.isArray(answer.json_value)
                    ? answer.json_value.map((item) => item.name)
                    : [];
            } else if (field.field_type === 'tree_select') {
                return Object.entries(answer.json_value)
                    .filter(([, { checked, partialChecked }]) => checked && !partialChecked)
                    .map(([key]) => key);
            }
            return answer.json_value;
        },

        // Builds a postAnswers payload from a set of answers, scoped to
        // whichever templateCategory field defs are passed in -- the full
        // template for saveAnswerBank, or just one section's fields for
        // confirmSection's Live-profile save.
        buildAnswerPayload(answers, templateCategories) {
            return answers
                .flatMap((category) => category.data)
                .reduce((acc, answer) => {
                    let value;
                    templateCategories.forEach((templateCategory) => {
                        templateCategory.data.forEach((field) => {
                            if (field.id === answer.id) {
                                value = this.getFieldPayloadValue(field, answer);
                            }
                        });
                    });
                    if (value !== undefined) acc[answer.id] = value;
                    return acc;
                }, {});
        },

        saveAnswerBank() {
            this.newAnswerBankData = this.buildAnswerPayload(this.answers, this.templates);

            const fileUploadFields = this.templates
                .flatMap((category) => category.data)
                .filter((field) => field.field_type === 'file_upload');

            const fileUploadFieldIds = fileUploadFields.map((field) => field.id);

            const payload = Object.fromEntries(
                Object.entries(this.newAnswerBankData).filter(([key]) => !fileUploadFieldIds.includes(key))
            );

            const callSave = this.hasErrors;
            if (!callSave) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error while creating data',
                    detail: 'Missing Information',
                    life: 3000,
                });
                return;
            }

            // Phase 6 publish gate: a Live profile that's about to save an
            // empty Required field gets a confirmation dialog. The actual
            // save is deferred until the user opts in to the Draft flip
            // (via confirmDraftAndSave) or cancels (via cancelDraftConfirm).
            if (this.publishGateEnabled && this.isPublished) {
                const flagged = this.findRequiredAboutToBeEmpty(payload);
                if (flagged.length > 0) {
                    this.draftConfirmFields = flagged;
                    this.pendingSavePayload = payload;
                    this.draftConfirmOpen = true;
                    return;
                }
            }

            this.postAnswers(payload);
        },

        async getTemplateForQuestionSet() {
            let qsid = localStorage.getItem('qsid');
            const response = await this.answerBankService.getTemplateById(qsid);
            localStorage.setItem('template', JSON.stringify(response.data.data));
            this.templates = response?.data?.data?.data || [];
            this.publishGateEnabled = !!response?.data?.data?.publish_gate_enabled;
            // Log total questions and questions per category
            let totalQuestions = 0;
            this.templates.forEach(category => {
                const categoryQuestionCount = category.data.length;
                totalQuestions += categoryQuestionCount;
            });

            // Phase 6: only question_sets with the publish gate on need
            // the Draft/Live row. Skipping the call for BPO keeps it from
            // touching the publication table at all.
            if (this.publishGateEnabled) {
                await this.fetchPublicationStatus();
                await this.fetchSectionStatus();
            } else {
                this.isPublished = false;
                this.lastPublishedAt = null;
                this.sectionStatus = null;
                this.sectionStatusByName = {};
            }
        },

        async fetchPublicationStatus() {
            const qsid = localStorage.getItem('qsid');
            try {
                const response = await this.answerBankService.getPublicationStatus(qsid);
                const data = response?.data?.data || {};
                this.isPublished = !!data.is_published;
                this.lastPublishedAt = data.last_published_at || null;
            } catch (err) {
                console.error('Failed to fetch publication status', err);
                this.isPublished = false;
                this.lastPublishedAt = null;
            }
        },

        // Per-section confirmation state. Called on mount (after templates
        // load) and re-called after every answer save so the is_current flag
        // reflects fresh edits.
        async fetchSectionStatus() {
            const qsid = localStorage.getItem('qsid');
            if (!qsid) return;
            try {
                const response = await this.answerBankService.getSectionStatus(qsid);
                const data = response?.data?.data || null;
                this.sectionStatus = data;
                const byName = {};
                if (data && Array.isArray(data.sections)) {
                    for (const s of data.sections) {
                        byName[s.header] = s;
                    }
                }
                this.sectionStatusByName = byName;
            } catch (err) {
                console.error('Failed to fetch section confirmation status', err);
                this.sectionStatus = null;
                this.sectionStatusByName = {};
            }
        },

        // Returns the section's current state for the given templates row,
        // or a stub if there is no matching row (defensive — happens when a
        // category is newly seeded and GET /sections hasn't been re-called).
        getSectionState(item) {
            if (!item) return null;
            return this.sectionStatusByName[item.category_name] || null;
        },

        // Count of Required-empty questions in a section. Today this is the
        // only "blocking" state; once AI pre-population ships, amber answers
        // (medium confidence) will also block until the vendor reviews them.
        countSectionBlocking(item) {
            if (!item || !Array.isArray(item.data)) return 0;
            const categoryAnswers = this.answers.find(a => a.category_name === item.category_name);
            const answerMap = new Map((categoryAnswers?.data || []).map(a => [a.id, a]));
            let missing = 0;
            for (const q of item.data) {
                if (!q.required) continue;
                const answer = answerMap.get(q.id);
                if (!answer || !this.isAnswerProvided(answer)) missing += 1;
            }
            return missing;
        },

        // Direct one-click confirm. The vendor's identity is already known
        // from the session token, so we don't prompt — just POST and refresh.
        // For re-confirmations on a currently-confirmed section we stamp a
        // "Re-confirmed — no changes" note so the audit history is readable.
        //
        // On a LIVE profile, Confirm is also the ONLY way this section's
        // edits get saved — the global Save button is hidden once
        // isPublished (see showGlobalSaveCancel), so an edited answer sits
        // unsaved in local state until the vendor clicks Confirm here. This
        // saves just this section's fields (buildAnswerPayload scoped to
        // templateCategory, not the whole profile) immediately before
        // writing the confirmation row, so a rejected/failed save never
        // gets falsely marked confirmed. On a Draft profile the normal
        // Save button already persisted the edit, so this is unchanged --
        // confirm-only, no save.
        async confirmSection(item) {
            const state = this.getSectionState(item);
            if (!state || !state.category_id) {
                console.warn('No category_id for section', item?.category_name);
                return;
            }
            if (this.countSectionBlocking(item) > 0) {
                // Footer message already tells the vendor what to fix.
                return;
            }
            const categoryName = item.category_name;
            if (this.sectionConfirmingByName[categoryName]) return;

            const qsid = parseInt(localStorage.getItem('qsid'));
            const confirmedBy = this.getCurrentUserDisplayName() || 'Vendor';
            const notes = state.is_current ? 'Re-confirmed — no changes' : null;

            this.sectionConfirmingByName = {
                ...this.sectionConfirmingByName,
                [categoryName]: true,
            };
            try {
                if (this.publishGateEnabled && this.isPublished) {
                    const templateCategory = this.templates.find(
                        (t) => t.category_name === categoryName
                    );
                    if (templateCategory) {
                        const sectionPayload = this.buildAnswerPayload(this.answers, [templateCategory]);
                        const fileUploadFieldIds = templateCategory.data
                            .filter((field) => field.field_type === 'file_upload')
                            .map((field) => field.id);
                        const filteredPayload = Object.fromEntries(
                            Object.entries(sectionPayload).filter(([key]) => !fileUploadFieldIds.includes(key))
                        );
                        if (Object.keys(filteredPayload).length > 0) {
                            const saveRes = await this.answerBankService.postAnswers(filteredPayload, qsid);
                            if (!(saveRes?.status === 200)) {
                                throw new Error('Failed to save section answers before confirming');
                            }
                        }
                    }
                }

                await this.answerBankService.postSectionConfirm(
                    qsid,
                    state.category_id,
                    confirmedBy,
                    notes
                );
                this.$toast?.add({
                    severity: 'success',
                    summary: 'Section confirmed',
                    detail: `${categoryName} is now confirmed.`,
                    life: 3000,
                });
                await this.getAnswers();
                await this.fetchSectionStatus();
            } catch (err) {
                console.error('Failed to confirm section', err);
                this.$toast?.add({
                    severity: 'error',
                    summary: 'Confirmation failed',
                    detail: 'Could not record this confirmation. Please try again.',
                    life: 4000,
                });
            } finally {
                this.sectionConfirmingByName = {
                    ...this.sectionConfirmingByName,
                    [categoryName]: false,
                };
            }
        },

        // Best-effort display name for the confirmed_by field. Tries the
        // CurrentUserService first, falls back to email local-part.
        getCurrentUserDisplayName() {
            try {
                const u = this.userService?.marketplaceUser || {};
                if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
                if (u.displayName) return u.displayName;
                if (u.email) return String(u.email).split('@')[0];
            } catch (_) { /* ignore */ }
            return '';
        },

        // Walks every Required question across every section and returns
        // the ones whose current saved answer is empty. Used by the
        // pre-flight check that fires before a publish API call -- the
        // gate's promise is "Live profile has every Required answered,"
        // and this method is what enforces it.
        runPreflight() {
            const missing = [];
            for (const templateCategory of this.templates) {
                const categoryAnswers = this.answers.find(a => a.category_name === templateCategory.category_name);
                const answerMap = new Map((categoryAnswers?.data || []).map(a => [a.id, a]));
                for (const question of templateCategory.data) {
                    if (!question.required) continue;
                    const answer = answerMap.get(question.id);
                    if (!answer || !this.isAnswerProvided(answer)) {
                        missing.push({
                            id: question.id,
                            section_name: templateCategory.category_name,
                            display_name: question.display_name,
                        });
                    }
                }
            }
            return missing;
        },

        onPublishClick() {
            // Unsaved edits in the form are NOT visible to the pre-flight
            // check (which reads this.answers, the last-saved snapshot).
            // Publishing while dirty would risk a LIVE profile that does
            // not match what is on screen, so we block and route the user
            // through Save first. Simpler failure mode than auto-saving.
            if (this.rootDirtyInd) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Unsaved changes',
                    detail: 'Save your changes first, then publish.',
                    life: 4000,
                });
                return;
            }

            const missing = this.runPreflight();
            if (missing.length > 0) {
                this.preFlightMissing = missing;
                this.preFlightDialogOpen = true;
                return;
            }
            // Phase D: section-confirmation gate. Even if every Required field
            // is answered, we hold publish until every is_required_section has
            // a current confirmation. The server enforces the same gate; this
            // is the friendly client-side preview.
            const sectionsBlocking = this.runSectionGateCheck();
            if (sectionsBlocking.length > 0) {
                this.sectionGateDialogOpen = true;
                return;
            }
            this.confirmPublishOpen = true;
        },

        // Phase D: returns the list of required sections that don't have a
        // current confirmation, used by the gate dialog. Empty list = ready
        // to publish.
        runSectionGateCheck() {
            const sections = this.sectionStatus?.sections || [];
            return sections.filter((s) => s.is_required_section && !s.is_current);
        },

        closeSectionGateDialog() {
            this.sectionGateDialogOpen = false;
        },

        // Scrolls the page to the named section's accordion tab and opens it.
        // Used by both pre-flight dialogs to make the click-to-jump UX work.
        jumpToSection(categoryName) {
            this.sectionGateDialogOpen = false;
            const headers = document.querySelectorAll('.accordion-header span');
            for (const span of headers) {
                if (span.textContent?.trim() === categoryName) {
                    span.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const tabLink = span.closest('.p-accordion-header')?.querySelector('a');
                    if (tabLink && tabLink.getAttribute('aria-expanded') !== 'true') {
                        tabLink.click();
                    }
                    break;
                }
            }
        },

        async confirmPublish() {
            const qsid = localStorage.getItem('qsid');
            try {
                const response = await this.answerBankService.setPublicationStatus(qsid, true);
                const data = response?.data?.data || {};
                this.isPublished = !!data.is_published;
                this.lastPublishedAt = data.last_published_at || this.lastPublishedAt;
                this.confirmPublishOpen = false;
                this.$toast.add({
                    severity: 'success',
                    summary: 'Profile published',
                    detail: 'Your profile is now visible to clients.',
                    life: 3000,
                });
            } catch (err) {
                console.error('Publish failed', err);
                // 422 with error: section_confirmation_gate is the Lambda's
                // signal that one or more required sections aren't confirmed
                // yet. Show the section-gate dialog so the vendor sees which
                // ones, instead of a generic error toast.
                const status = err?.response?.status;
                const errorCode = err?.response?.data?.error;
                if (status === 422 && errorCode === 'section_confirmation_gate') {
                    this.confirmPublishOpen = false;
                    // Refresh section state in case it changed server-side
                    // (e.g. an answer save between the gate preview and the
                    // publish click).
                    await this.fetchSectionStatus();
                    this.sectionGateDialogOpen = true;
                    return;
                }
                this.$toast.add({
                    severity: 'error',
                    summary: 'Publish failed',
                    detail: 'Please try again.',
                    life: 3000,
                });
            }
        },

        cancelPublishConfirm() {
            this.confirmPublishOpen = false;
        },

        // Closes the pre-flight blocker, jumps the user to the chosen
        // missing question: opens the section accordion + scrolls to it.
        jumpToQuestion(item) {
            this.preFlightDialogOpen = false;
            this.$nextTick(() => {
                const sectionHeaderText = item.section_name;
                const headers = document.querySelectorAll('.accordion-header span');
                for (const span of headers) {
                    if (span.textContent && span.textContent.trim() === sectionHeaderText) {
                        const accordionLink = span.closest('.p-accordion-header-link');
                        if (accordionLink && accordionLink.getAttribute('aria-expanded') === 'false') {
                            accordionLink.click();
                        }
                        setTimeout(() => {
                            const allLabels = document.querySelectorAll('.field-title');
                            for (const labelEl of allLabels) {
                                if (labelEl.textContent.includes(item.display_name)) {
                                    labelEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    labelEl.classList.add('field-flash');
                                    setTimeout(() => labelEl.classList.remove('field-flash'), 1800);
                                    break;
                                }
                            }
                        }, 350);
                        break;
                    }
                }
            });
        },

        // Compares the about-to-save payload against the current template
        // and answers, returning the Required questions whose value in
        // the payload would be empty. Drives the "this will return your
        // profile to Draft" confirmation when a Live profile is editing
        // a Required field to nothing.
        findRequiredAboutToBeEmpty(payload) {
            const flagged = [];
            for (const templateCategory of this.templates) {
                for (const question of templateCategory.data) {
                    if (!question.required) continue;
                    const value = payload[question.id];
                    if (this.isPayloadValueEmpty(value)) {
                        flagged.push({
                            id: question.id,
                            section_name: templateCategory.category_name,
                            display_name: question.display_name,
                        });
                    }
                }
            }
            return flagged;
        },

        // Lightweight emptiness check for payload values. Payload shape
        // is { [questionId]: value } where value is already extracted
        // per field_type by saveAnswerBank (string for selects, array
        // for multi, string for long_text, object/array for tables).
        isPayloadValueEmpty(value) {
            if (value === null || value === undefined) return true;
            if (typeof value === 'string') return value.trim() === '';
            if (Array.isArray(value)) return value.length === 0;
            if (typeof value === 'object') return Object.keys(value).length === 0;
            return false;
        },

        async confirmDraftAndSave() {
            const qsid = localStorage.getItem('qsid');
            try {
                await this.answerBankService.setPublicationStatus(qsid, false);
                this.isPublished = false;
                this.draftConfirmOpen = false;
                if (this.pendingSavePayload) {
                    this.postAnswers(this.pendingSavePayload);
                    this.pendingSavePayload = null;
                }
                this.$toast.add({
                    severity: 'info',
                    summary: 'Returned to Draft',
                    detail: 'Re-publish when ready.',
                    life: 3000,
                });
            } catch (err) {
                console.error('Draft transition failed', err);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not return to Draft',
                    detail: 'Please try again.',
                    life: 3000,
                });
            }
        },

        cancelDraftConfirm() {
            this.draftConfirmOpen = false;
            this.draftConfirmFields = [];
            this.pendingSavePayload = null;
        },

        async getAnswers() {
            const response = await this.answerBankService?.getAnswers(localStorage.getItem('qsid'));

            if (response?.status === 200) {
                this.answers = response?.data?.data?.data?.map((category) => {
                    return {
                        ...category,
                        data: category.data.map((answer) => {
                            let modifiedAnswer = { ...answer };
                            this.templates?.forEach((templateCategory) => {
                                templateCategory.data.forEach((field) => {
                                    if (field.id === answer.id) {
                                        if (field.field_type === 'single_select' || field.field_type === 'single_select_search') {
                                            modifiedAnswer.json_value = { name: answer.json_value };
                                        } else if (field.field_type === 'boolean_select') {
                                            modifiedAnswer.json_value =
                                                answer.json_value === 'true' || answer.json_value === true
                                                    ? { name: 'Yes', value: true }
                                                    : answer.json_value === 'false' || answer.json_value === false
                                                        ? { name: 'No', value: false }
                                                        : null;
                                        } else if (
                                            field.field_type === 'multi_select' ||
                                            field.field_type === 'multi_select_search'
                                        ) {
                                            if (Array.isArray(answer.json_value)) {
                                                modifiedAnswer.json_value = answer.json_value.map((value) => ({ name: value }));
                                            }
                                        } else if (field.field_type === 'tree_select') {
                                            const optionsObj = {};
                                            answer.json_value?.forEach((sl) => {
                                                optionsObj[sl] = { checked: true, partialChecked: false };
                                            });
                                            modifiedAnswer.json_value = optionsObj;
                                        } else if (
                                            field.field_type === 'number' ||
                                            field.field_type === 'short_text' ||
                                            field.field_type === 'decimal'
                                        ) {
                                            modifiedAnswer.json_value = answer.json_value === 'null' ? null : answer.json_value;
                                        } else {
                                            modifiedAnswer.json_value = answer.json_value === 'null' ? null : answer.json_value;
                                        }
                                    }
                                });
                            });
                            return modifiedAnswer;
                        }),
                    };
                }) || [];
                this.origin = response?.data?.data?.origin || null;
                this.distributorValues = response?.data?.data?.distributors || null;
                this.hasAgreement = await this.partnerDetailsService.checkIfAdminHasAgreementWithVendor(this.isChannel, this.defaultSubnetworkId, this.distributorValues)
                console.log("Has Agreement::", this.hasAgreement)
                console.log('Answers Length:-', this.answers.forEach(answer => {
                    console.log('Answer:-', answer);
                }));
            }
            // Tear down the directory-switch spinner once both the
            // template and the answers are loaded; the watch on
            // templates fires getAnswers, so reaching here means both
            // fetches resolved (success or otherwise) and the accordion
            // is safe to render.
            this.isLoadingDirectory = false;
        },

        postAnswers(payload) {
            this.answerBankService.postAnswers(payload, localStorage.getItem('qsid'))
                .then((res) => {
                    if (res && res.status === 200) {
                        // Without this the Save button stays visible after a
                        // successful save because rootDirtyInd is what gates
                        // v-show on the Cancel/Save action group at the top.
                        this.rootDirtyInd = false;
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Changes saved',
                            detail: 'Your answers are up to date.',
                            life: 3000,
                        });
                        // Re-fetch answers so locally-cached provenance
                        // (source / requires_review / confidence_band) picks
                        // up the server's post-save state -- an edited field
                        // flips to source='vendor_edited' in the DB, but
                        // updateAnswers() only ever mutated json_value
                        // in-memory, so without this refetch the AI-inferred
                        // amber styling kept showing indefinitely after save.
                        this.getAnswers();

                        // Edits may have invalidated a confirmed section.
                        // Re-fetch so the per-section pills + can_publish
                        // summary reflect the new last_answer_edit_at.
                        if (this.publishGateEnabled) {
                            this.fetchSectionStatus();
                        }
                    } else {
                        // Handle non-200 status responses
                        let errorMsg = 'Error while creating data';
                        if (res && res.data) {
                            errorMsg = res.data?.data || res.data?.message || res.data || errorMsg;
                        }
                        this.$toast.add({ 
                            severity: 'error', 
                            summary: 'Error', 
                            detail: errorMsg, 
                            life: 5000 
                        });
                    }
                })
                .catch((error) => {
                    // Handle error response - axios throws errors for 4xx and 5xx status codes
                    console.error('Error in postAnswers:', error);
                    console.error('Error response:', error.response);
                    console.error('Error response data:', error.response?.data);
                    console.error('Error response status:', error.response?.status);
                    
                    let errorMsg = 'An error occurred while saving the data.';
                    
                    if (error.response && error.response.data) {
                        const responseData = error.response.data;
                        console.log('Response data type:', typeof responseData);
                        console.log('Response data:', responseData);
                        
                        // Error structure: { statusCode: 400, message: "Bad Request", data: "error message" }
                        // Try to get the actual error message from the data field
                        if (responseData && typeof responseData === 'object' && responseData.data) {
                            // If responseData is an object with a 'data' property containing the message
                            errorMsg = responseData.data;
                        } else if (responseData && typeof responseData === 'object' && responseData.message) {
                            errorMsg = responseData.message;
                        } else if (typeof responseData === 'string') {
                            // If responseData is directly a string
                            errorMsg = responseData;
                        } else if (responseData) {
                            // Fallback: stringify the response
                            errorMsg = JSON.stringify(responseData);
                        }
                    } else if (error.message) {
                        errorMsg = error.message;
                    }
                    
                    console.log('Showing error toast with message:', errorMsg);
                    this.$toast.add({ 
                        severity: 'error', 
                        summary: 'Error', 
                        detail: errorMsg, 
                        life: 5000 
                    });
                });
        },

        isNewCategory(createdOn) {
            if (!createdOn) return false;
            try {
                const createdDate = new Date(createdOn);
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                return createdDate > sixMonthsAgo;
            } catch (error) {
                console.error('Error parsing date:', error);
                return false;
            }
        },

        getCompletionText(categoryName) {
            // ✅ OPTIMIZED: Use cache for completion text
            const cacheKey = `text_${categoryName}`;
            if (this.categoryCompletionCache[cacheKey] &&
                Date.now() - (this.categoryCompletionCache[cacheKey].time || 0) < 500) {
                return this.categoryCompletionCache[cacheKey].value;
            }

            const categoryAnswers = this.answers.find(a => a.category_name === categoryName);
            if (!categoryAnswers) return '0 of 0 Questions Answered';

            const templateCategory = this.templates.find(t => t.category_name === categoryName);
            if (!templateCategory) return '0 of 0 Questions Answered';

            const totalQuestions = templateCategory.data.length;

            // Create a map of question IDs to their answers
            const answerMap = new Map(categoryAnswers.data.map(answer => [answer.id, answer]));

            const answeredQuestions = templateCategory.data.filter(question => {
                const answer = answerMap.get(question.id);
                if (!answer) return false;
                return this.isAnswerProvided(answer);
            }).length;

            const result = `${answeredQuestions} of ${totalQuestions} Questions Answered`;

            // Cache the result
            this.categoryCompletionCache[cacheKey] = {
                value: result,
                time: Date.now()
            };

            return result;
        },

        getCompletionClass(categoryName) {
            // ✅ OPTIMIZED: Use cache for completion class
            const cacheKey = `class_${categoryName}`;
            if (this.categoryCompletionCache[cacheKey] &&
                Date.now() - (this.categoryCompletionCache[cacheKey].time || 0) < 500) {
                return this.categoryCompletionCache[cacheKey].value;
            }

            const categoryAnswers = this.answers.find(a => a.category_name === categoryName);
            if (!categoryAnswers) return 'low';

            const totalQuestions = this.templates.find(t => t.category_name === categoryName)?.data.length || 0;
            const answeredQuestions = categoryAnswers.data.filter(answer => this.isAnswerProvided(answer)).length;

            const completionRate = (answeredQuestions / totalQuestions) * 100;

            let result;
            if (completionRate < 50) result = 'low';
            else if (completionRate < 75) result = 'medium';
            else result = 'high';

            // Cache the result
            this.categoryCompletionCache[cacheKey] = {
                value: result,
                time: Date.now()
            };

            return result;
        },

        getCategoryCompletionCounts(templateCategory, categoryAnswers) {
            const validQuestionIds = new Set(templateCategory.data.map(question => question.id));
            const questionsCount = templateCategory.data.length;
            const answeredCount = categoryAnswers.data.filter(answer => validQuestionIds.has(answer.id) && this.isAnswerProvided(answer)).length;

            return { questionsCount, answeredCount };
        },

        isAnswerProvided(answer) {
            if (!answer) return false;

            const value = answer?.json_value;
            const attachments = Array.isArray(answer?.other?.attachments)
                ? answer.other.attachments.filter(att => typeof att === 'string' && att.trim() !== '')
                : [];
            const hasAttachments = attachments.length > 0;

            // File upload only (json_value null, attachment present)
            if (value === null || value === undefined) {
                return hasAttachments;
            }

            // Treat zero as empty unless a file was uploaded
            if (typeof value === 'number') {
                if (value === 0) {
                    return hasAttachments;
                }
                return true;
            }

            // Booleans represent valid answers (Yes/No)
            if (typeof value === 'boolean') {
                return true;
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (!trimmed) return hasAttachments;

                const lowered = trimmed.toLowerCase();
                if (lowered === 'null' || lowered === 'undefined') {
                    return hasAttachments;
                }

                if (trimmed === '{}' || trimmed === '[]' || trimmed === '0') {
                    return hasAttachments;
                }

                return true;
            }

            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return hasAttachments;
                }

                return value.some(item => {
                    if (item === null || item === undefined) return false;

                    if (typeof item === 'string') {
                        const trimmed = item.trim();
                        if (!trimmed) return false;
                        if (trimmed === '0') return false;
                        return true;
                    }

                    if (typeof item === 'object') {
                        if ('name' in item) {
                            const name = item.name;
                            if (name === null || name === undefined) return false;
                            if (typeof name === 'string' && name.trim() === '') return false;
                            return true;
                        }
                        if ('value' in item) {
                            const val = item.value;
                            if (val === null || val === undefined) return false;
                            if (typeof val === 'number' && val === 0) return false;
                            if (typeof val === 'string' && val.trim() === '') return false;
                            if (typeof val === 'string' && val.trim() === '0') return false;
                            return true;
                        }
                        // Generic object-with-primitive-values shape
                        // (e.g. EWP global delivery footprint rows: {country,
                        // total_engineers, in_office_pct, ...}). Treat the
                        // item as answered when at least one field has a
                        // non-empty primitive value.
                        return Object.values(item).some(v => {
                            if (v === null || v === undefined) return false;
                            if (typeof v === 'string') return v.trim() !== '';
                            if (typeof v === 'number') return v !== 0;
                            if (typeof v === 'boolean') return true;
                            if (Array.isArray(v)) return v.length > 0;
                            return false;
                        });
                    }

                    return false;
                });
            }

            if (typeof value === 'object') {
                const keys = Object.keys(value);
                if (!keys.length) {
                    return hasAttachments;
                }

                if ('name' in value) {
                    const name = value.name;
                    if (name === null || name === undefined) {
                        return hasAttachments;
                    }
                    if (typeof name === 'string' && name.trim() === '') {
                        return hasAttachments;
                    }
                    if (name === 0 || name === '0') {
                        return hasAttachments;
                    }
                    return true;
                }

                if ('value' in value) {
                    const val = value.value;
                    if (val === null || val === undefined) {
                        return hasAttachments;
                    }
                    if (typeof val === 'number' && val === 0) {
                        return hasAttachments;
                    }
                    if (typeof val === 'string' && val.trim() === '') {
                        return hasAttachments;
                    }
                    if (typeof val === 'string' && val.trim() === '0') {
                        return hasAttachments;
                    }
                    return true;
                }

                // Tree select style value: { optionKey: { checked, partialChecked } }
                const hasCheckedNode = Object.values(value).some(node => {
                    return node && node.checked === true && node.partialChecked !== true;
                });
                if (hasCheckedNode) {
                    return true;
                }

                // Generic object-with-primitive-values shape (e.g. EWP
                // role_distribution / seniority_profile / ai_proficiency
                // answers: { software_engineering: "None", qa_test_engineering:
                // "1-25%", ... }). Treat as answered when at least one key
                // has a non-empty primitive value.
                const hasPrimitiveValue = Object.values(value).some(v => {
                    if (v === null || v === undefined) return false;
                    if (typeof v === 'string') return v.trim() !== '';
                    if (typeof v === 'number') return v !== 0;
                    if (typeof v === 'boolean') return true;
                    if (Array.isArray(v)) return v.length > 0;
                    return false;
                });
                if (hasPrimitiveValue) {
                    return true;
                }

                return hasAttachments;
            }

            return hasAttachments;
        },
    },
};
</script>

<style scoped lang="scss">


.directoryTitle {
    font-size: 1.5rem;
    margin-left: 1rem;
}

::v-deep(.p-accordion-header) {
    font-size: large;
}

::v-deep(.p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link),
::v-deep(.p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link):hover {
    background-color: rgb(228, 228, 228);
    font-weight: 500;
    margin-top: 1rem;
}

::v-deep(.p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link) {
    /* Section-header background reflects completion state. Soft pastel
       palette (Tailwind *-100/*-200 range) so the reds/yellows/greens
       don't fight the pending pill or the NEW badge, and dark text
       stays readable in every state. */
    &:has(.completion-indicator.low) {
        background-color: #fca5a5;   /* rose — mostly incomplete */
    }
    &:has(.completion-indicator.medium) {
        background-color: #fcd34d;   /* warm amber — in progress */
    }
    &:has(.completion-indicator.high) {
        background-color: #86efac;   /* mint — mostly complete */
    }
}

.completion-indicator {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
    margin-left: auto;
    white-space: nowrap;
}

.completion-indicator.low {
    background-color: var(--acx-dark-orange);
}

.completion-indicator.medium {
    background-color: var(--acx-dark-yellow);
}

.completion-indicator.high {
    background-color: var(--acx-pale-green);
}

::v-deep(.p-button) {
    margin: 0 0 1.25rem 1.25rem;
    width: 8rem !important;
    background: #00796b !important;
    border: 2px solid #00796b !important;
}

::v-deep(.p-button.p-button-outlined) {
    background-color: transparent !important;
    color: #00796b !important;
    border: 1px solid;
}

.profile-title {
    font-weight: 700;
    font-family: 'Prompt', sans-serif;
    font-size: 27px;
    line-height: 1;
    margin: 0 0 0 1rem;
}

.radio-button-container {
    margin-top: 0.5rem;
    margin-left: 1rem;
    display: flex;
    align-items: center;
}

.field-radiobutton {
    display: flex;
    align-items: center;
    margin-right: 1rem;
    margin-bottom: 0.5rem;
}

.field-radiobutton label {
    margin-left: 0.5rem;
}

.profile-completion-container {
    margin-right: 1rem;
    margin-bottom: 2rem;
}

.profile-completion-text {
    font-size: 1.5rem;
    font-weight: 500;
}

::v-deep(.p-progressbar) {
    background: #e9ecef;
}

::v-deep(.p-progressbar .p-progressbar-value) {
    background: #6dbd92;
}


::v-deep(.p-progressbar .p-progressbar-label) {
    display: none;
}

.accordion-header {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: space-between;
    width: 95%;
}

.new-badge {
    background-color: #6dbd92;
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: bold;
    text-transform: uppercase;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.05);
    }

    100% {
        transform: scale(1);
    }
}

.section-purpose-strip {
    border-left: 3px solid #14b8a6;
    background: #f8fafc;
    color: #475569;
    font-style: italic;
    padding: 0.625rem 0.875rem;
    margin: 0 0 1rem;
    border-radius: 0 4px 4px 0;
    line-height: 1.45;
}

/* Section-confirmation ornaments (project_se_section_confirmation_publish_gate).
   Header pills + per-section footer button + dialog form. */

.accordion-header-right {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: nowrap;
}

.section-confirmed-pill {
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #6ee7b7;
    border-radius: 999px;
    padding: 0.125rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
}

.section-blocking-pill {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fcd34d;
    border-radius: 999px;
    padding: 0.125rem 0.625rem;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
}

.section-confirm-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 0.75rem;
    padding: 0.5rem 0.875rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.section-confirm-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1 1 auto;
    min-width: 0;
}

.section-confirm-status i {
    flex-shrink: 0;
}

.section-confirm-warning {
    color: #92400e;
    font-size: 0.875rem;
    line-height: 1.4;
}

.section-confirm-confirmed {
    color: #166534;
    font-size: 0.875rem;
    line-height: 1.4;
}

.section-confirm-prompt {
    color: #475569;
    font-size: 0.875rem;
    line-height: 1.4;
}

.section-confirm-button,
button.section-confirm-button,
button.section-confirm-button.p-button,
button.section-confirm-button.p-button.p-component {
    flex-shrink: 0 !important;
    flex-grow: 0 !important;
    align-self: center !important;
    white-space: nowrap !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 1rem !important;
    height: 32px !important;
    min-height: 0 !important;
    max-height: 32px !important;
    line-height: 1 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.section-confirm-button .p-button-label,
button.section-confirm-button.p-button .p-button-label,
button.section-confirm-button.p-button.p-component .p-button-label {
    padding: 0 !important;
    margin: 0 !important;
    line-height: 1 !important;
    height: auto !important;
    display: inline-flex !important;
    align-items: center !important;
}

.section-confirm-button .p-button-label {
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    flex: 0 0 auto !important;
}

.section-confirm-form {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.5rem;
}

.section-confirm-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: #334155;
}

.section-confirm-label-spaced {
    margin-top: 0.875rem;
}

.section-confirm-optional {
    color: #94a3b8;
    font-weight: 400;
}

/* Phase D sticky publish footer. Fixed to the bottom of the viewport;
   sits above any page content with a soft shadow so it always feels
   present without obscuring the accordion below. */
/* Bottom padding on the page so the last accordion isn't covered by the
   fixed footer when the gate is on. */
.answerbank-with-publish-footer {
    padding-bottom: 80px;
}

.profile-publish-footer {
    position: fixed;
    bottom: 0;
    /* Clear the left navigation sidebar so the counter doesn't sit under it.
       The app exposes --layout-sidebar-width on some themes; fall back to a
       sensible 280px if not set. */
    left: var(--layout-sidebar-width, 238px);
    right: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 0.875rem 1.5rem;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.06);
}

/* When the sidebar collapses (some Sakai/PrimeVue templates toggle body
   classes for this), use a slimmer offset. */
body.layout-static-inactive .profile-publish-footer,
body.layout-overlay-active .profile-publish-footer {
    left: 0;
}

.profile-publish-footer-left {
    display: flex !important;
    align-items: center !important;
    gap: 1rem !important;
    flex: 1 1 auto !important;
    min-width: 0;
    overflow: visible !important;
}

.profile-publish-footer-counter,
span.profile-publish-footer-counter,
.profile-publish-footer .profile-publish-footer-counter {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    font-size: 0.9rem !important;
    font-weight: 600 !important;
    color: #1f2937 !important;
    white-space: nowrap !important;
    line-height: 1.2 !important;
    flex: 0 0 auto !important;
}

.profile-publish-footer-right {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
}

.profile-publish-footer-live-text {
    font-size: 0.85rem;
    font-weight: 700;
    color: #15803d;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.profile-publish-footer-button,
button.profile-publish-footer-button,
button.profile-publish-footer-button.p-button.p-component {
    margin: 0 !important;
    padding: 0.5rem 1.25rem !important;
    height: 38px !important;
    min-height: 0 !important;
    line-height: 1 !important;
    width: auto !important;
}

/* Force the Save-and-return-to-Draft button label onto a single line.
   The default PrimeVue width constraint plus the longer label causes
   the text to wrap by default. */
.draft-save-button,
button.draft-save-button,
button.draft-save-button.p-button.p-component {
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    white-space: nowrap !important;
    padding: 0.5rem 1.25rem !important;
    height: auto !important;
    min-height: 0 !important;
    line-height: 1.2 !important;
}

.draft-save-button .p-button-label,
button.draft-save-button.p-button .p-button-label,
button.draft-save-button.p-button.p-component .p-button-label {
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    padding: 0 !important;
}

/* Phase 6 publish-gate ornaments: status pill next to title, Publish
   button (teal to match Required-to-publish badges), and pre-flight
   modal styling. All gated behind publishGateEnabled in the template
   so they never paint for BPO or other untoggled question_sets. */
.profile-status-pill {
    display: inline-block;
    padding: 0.2rem 0.7rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    vertical-align: middle;
}
.profile-status-pill.live {
    background: #ccfbf1;
    color: #0f766e;
}
.profile-status-pill.draft {
    background: #e2e8f0;
    color: #475569;
}

/* Draft/Live status banner -- spells out what the header pill means and
   what to do next (draft) or what's currently live (published). */
.profile-status-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    border-radius: 8px;
    border: 1px solid;
    padding: 0.75rem 1rem;
    margin: 0.75rem 0;
}
.profile-status-banner i {
    font-size: 1.1rem;
    margin-top: 0.15rem;
    flex-shrink: 0;
}
.profile-status-banner-title {
    margin: 0;
    font-weight: 700;
}
.profile-status-banner-detail {
    margin: 0.2rem 0 0;
    font-size: 0.9rem;
}
.profile-status-banner.draft {
    background: #fefce8;
    border-color: #fde68a;
    color: #854d0e;
}
.profile-status-banner.draft i {
    color: #b45309;
}
.profile-status-banner.live {
    background: #ecfdf5;
    border-color: #a7f3d0;
    color: #065f46;
}
.profile-status-banner.live i {
    color: #15803d;
}

/* Dialog body content -- title + footer come from PrimeVue Dialog
   defaults (header prop + #footer slot), so we only style the body
   bits that don't have a default. */
.preflight-subtitle {
    margin: 0 0 1rem;
    line-height: 1.4;
}
.preflight-list {
    max-height: 22rem;
    overflow-y: auto;
    margin-bottom: 0.5rem;
}
.preflight-group {
    margin-bottom: 0.85rem;
}
.preflight-group-label {
    font-weight: 600;
    margin: 0.5rem 0 0.35rem;
}
.preflight-item {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 0.4rem 0;
    color: var(--primary-color, #2563eb);
    cursor: pointer;
    line-height: 1.3;
}
.preflight-item:hover {
    text-decoration: underline;
}
.preflight-flat-list {
    padding-left: 1.1rem;
    margin: 0 0 1rem;
}
.preflight-flat-list li {
    margin-bottom: 0.3rem;
}

/* Brief highlight on the jumped-to question's label so the user can
   visually pick it out after the modal closes. */
.field-flash {
    animation: field-flash-anim 1.8s ease-out;
}
@keyframes field-flash-anim {
    0%   { background: #fef3c7; }
    100% { background: transparent; }
}

/* Single inline row beneath the page header: TreeSelect on the left,
   breadcrumb next to it, admin visibility toggle right-aligned. The
   page title ("Answer Bank") above already carries the section name,
   so we don't repeat it as a card label here. */
.directory-row {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    flex-wrap: wrap;
    margin: 0.5rem 0 1.25rem;
}
.directory-select {
    width: 28rem;
    max-width: 100%;
}
.directory-breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    color: #475569;
    font-size: 0.95rem;
}
.directory-breadcrumb .pi {
    color: #14b8a6;
    font-size: 1rem;
}
.visibility-toggle {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.6rem;
}
.visibility-label {
    font-size: 0.9rem;
    color: #0f172a;
    font-weight: 500;
}
.directory-empty-hint {
    margin: 0.25rem 0 0;
    color: #64748b;
    font-size: 0.9rem;
    font-style: italic;
}
.directory-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    padding: 3rem 0 4rem;
    color: #64748b;
    font-size: 0.95rem;
}
</style>
